import { useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import {
  businessOutline,
  chatbubblesOutline,
  phonePortraitOutline,
  printOutline,
  settingsOutline,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';

import DetailsList, { DetailsListItem } from '@components/DetailsList';
import EventHeader from '@components/EventHeader';
import { ButtonType } from '@components/Layout';
import { Spinner } from '@components/Spinner';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { usePrinter } from '@hooks/usePrinter';
import { CheckAction } from '@pages/Check/Enums';
import { formatDateRange } from '@services/date-service';
import { getEvent as getEventFlagsFn } from '@services/flag';
import { generateRoute } from '@services/navigation-service';
import {
  getCheckOutEnabled,
  getEventInfo,
  getKioskModeEnabled,
  setConfigurations,
  setEventInfo,
} from '@state/event';
import { setEvent as setEventFlags } from '@state/flag';
import { clearSessionInfo } from '@state/session';
import { getLocale } from '@state/system';

import { useEventAndConfiguration } from './queries';

const selector = createSelector(
  getKioskModeEnabled,
  getCheckOutEnabled,
  getEventInfo,
  getLocale,
  (kioskModeEnabled, checkOutEnabled, event, locale) => ({
    kioskModeEnabled,
    checkOutEnabled,
    event,
    locale,
  })
);

const Details = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { push: pushRoute } = useIonRouter();
  const { kioskModeEnabled, checkOutEnabled, event, locale } = useSelector(selector);
  const { enabled: printingEnabled } = usePrinter();

  const onEventClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.EventCheck, { eventId, action: CheckAction.CheckIn }));
  }, [eventId, pushRoute]);

  const onSessionClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.SessionList, { eventId }));
  }, [eventId, pushRoute]);

  const onPrintBadgeClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.PrintBadge, { eventId }));
  }, [eventId, pushRoute]);

  const onSetupKioskClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.KioskSetup, { eventId }));
  }, [eventId, pushRoute]);

  const onPrinterSettingsClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId }));
  }, [eventId, pushRoute]);

  if (!event) {
    return null;
  }

  let eventSubtitle = checkOutEnabled
    ? t`Scan attendees in or out of the event.`
    : t`Scan attendees in to the Event.`;
  if (printingEnabled) {
    eventSubtitle = `${eventSubtitle} ${t`You may also print badges while checking in.`}`;
  }

  return (
    <DetailsList
      pageTitle={event.name}
      buttonType={ButtonType.Menu}
      header={
        <EventHeader
          name={event.name}
          imageUrl={event.imageUrl}
          date={formatDateRange(event.startDate, event.endDate, locale)}
          location={event?.location}
        />
      }
      callToAction={t`What would you like to do?`}
      listItems={
        [
          {
            onClick: onEventClick,
            icon: businessOutline,
            title: checkOutEnabled ? t`Event Check In & Out` : t`Event Check In`,
            subtitle: eventSubtitle,
          },
          {
            onClick: onSessionClick,
            icon: chatbubblesOutline,
            title: checkOutEnabled ? t`Session Check In & Out` : t`Session Check In`,
            subtitle: t`Scan attendees into specific sessions.`,
          },
          printingEnabled && {
            onClick: onPrintBadgeClick,
            icon: printOutline,
            title: t`Print Badges only`,
            subtitle: t`Print attendee’s badges upon request.`,
          },
          kioskModeEnabled && {
            onClick: onSetupKioskClick,
            icon: phonePortraitOutline,
            title: t`Set up Kiosk`,
            subtitle: t`Allow attendees to check in by themselves.`,
          },
          printingEnabled && {
            onClick: onPrinterSettingsClick,
            icon: settingsOutline,
            title: t`Manage your device settings`,
            subtitle: t`Set up your printer for onsite badge printing.`,
          },
        ].filter(Boolean) as DetailsListItem[]
      }
    />
  );
};

const EventLoader = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const dispatch = useDispatch();
  const [isEventFlagsLoading, setIsEventFlagsLoading] = useState(true);

  const [
    { data: event, isFetched: isEventFetched, isFetching: isEventFetching },
    { data: configuration, isFetched: isConfigurationFetched, isFetching: isConfigurationFetching },
  ] = useEventAndConfiguration(eventId);

  useEffect(() => {
    const effect = async () => {
      const eventFlags = await getEventFlagsFn(eventId);
      dispatch(setEventFlags(eventFlags));
      setIsEventFlagsLoading(false);
    };
    // skipcq: JS-0328
    effect();
  }, [dispatch, eventId]);

  useEffect(() => {
    if (isEventFetched && event) {
      dispatch(
        setEventInfo({
          id: String(event.id),
          name: event.name,
          imageUrl: event?.eventIcon?.standardSizeUrl,
          startDate: event.startDate,
          endDate: event.endDate,
          location: event.locationName,
        })
      );
    }
  }, [dispatch, event, isEventFetched]);

  useEffect(() => {
    if (isConfigurationFetched && configuration) {
      dispatch(setConfigurations({ ...configuration }));
    }
  }, [configuration, dispatch, isConfigurationFetched]);

  useLoading(isEventFetching || isConfigurationFetching || isEventFlagsLoading);

  // If we are on event details then we need to clear the session state so that we correctly check
  // in/out from the event, instead of a session that may have been selected previously.
  useEffect(() => {
    dispatch(clearSessionInfo());
  }, [dispatch]);

  if (isEventFlagsLoading) {
    return <Spinner $centerFixed />;
  }

  return <Details />;
};

export default EventLoader;
